import { useState } from 'react';
import { api, type Category, type Item } from '../api';
import { run } from '../hooks';
import type { EditorProps } from '../pages/SitePage';

/** Categories and cards editor with per-category re-scan (planning.md FR-C-03, FR-C-04). */
export function SpringboardEditor({ bundle, reload, show }: EditorProps) {
  const { site, categories, items, scans } = bundle;
  const siteId = site.siteId;
  const [newLabel, setNewLabel] = useState('');
  const runningFor = new Set(scans.filter((s) => s.mode === 'category' && !['ready', 'failed'].includes(s.status)).map((s) => s.categoryId));

  const saveCategory = async (c: Category, patch: Partial<Category>) => {
    const r = await run(api.updateCategory(siteId, c.categoryId, patch), show);
    if (r) await reload();
  };
  const move = async (c: Category, dir: -1 | 1) => {
    const sorted = [...categories].sort((a, b) => a.order - b.order);
    const idx = sorted.findIndex((x) => x.categoryId === c.categoryId);
    const other = sorted[idx + dir];
    if (!other) return;
    await Promise.all([api.updateCategory(siteId, c.categoryId, { order: other.order }), api.updateCategory(siteId, other.categoryId, { order: c.order })]);
    await reload();
  };
  const remove = async (c: Category) => {
    if (!confirm(`Delete "${c.label}" and its ${items.filter((i) => i.categoryId === c.categoryId).length} cards?`)) return;
    const r = await run(api.deleteCategory(siteId, c.categoryId), show, 'Category deleted');
    if (r !== null) await reload();
  };
  const rescan = async (c: Category) => {
    const r = await run(api.rescan(siteId, c.categoryId), show, `Re-scanning "${c.label}"… a draft version will appear when done.`);
    if (r) await reload();
  };
  const addCategory = async () => {
    if (!newLabel.trim()) return;
    const r = await run(api.createCategory(siteId, { label: newLabel.trim() }), show, 'Category added');
    if (r) {
      setNewLabel('');
      await reload();
    }
  };

  return (
    <div>
      <p className="muted small" style={{ marginBottom: 16 }}>
        Categories are visitor intents; cards are the pages and calls to action inside them. Edits are saved immediately and go live when you publish. Text you
        edit is kept on re-scans.
      </p>
      {[...categories].sort((a, b) => a.order - b.order).map((c, idx) => (
        <CategoryBlock
          key={c.categoryId}
          category={c}
          items={items.filter((i) => i.categoryId === c.categoryId).sort((a, b) => a.order - b.order)}
          first={idx === 0}
          last={idx === categories.length - 1}
          rescanning={runningFor.has(c.categoryId)}
          onSave={(p) => saveCategory(c, p)}
          onMove={(d) => move(c, d)}
          onDelete={() => remove(c)}
          onRescan={() => rescan(c)}
          siteId={siteId}
          reload={reload}
          show={show}
        />
      ))}
      <div className="card row" style={{ marginTop: 8 }}>
        <input className="input" style={{ maxWidth: 320 }} placeholder="New category label" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} />
        <button className="btn" onClick={addCategory}>
          Add category
        </button>
      </div>
    </div>
  );
}

function CategoryBlock(props: {
  category: Category;
  items: Item[];
  first: boolean;
  last: boolean;
  rescanning: boolean;
  onSave: (p: Partial<Category>) => Promise<void>;
  onMove: (d: -1 | 1) => Promise<void>;
  onDelete: () => Promise<void>;
  onRescan: () => Promise<void>;
  siteId: string;
  reload: () => Promise<void>;
  show: (t: string, err?: boolean) => void;
}) {
  const { category: c, items } = props;
  const [label, setLabel] = useState(c.label);
  const [description, setDescription] = useState(c.description);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ title: '', url: '', description: '', ctaLabel: 'Open', itemType: 'page' as Item['itemType'] });
  const dirty = label !== c.label || description !== c.description;

  const addItem = async () => {
    if (!draft.title.trim() || !draft.url.trim()) return;
    const r = await run(api.createItem(props.siteId, { categoryId: c.categoryId, ...draft }), props.show, 'Card added');
    if (r) {
      setAdding(false);
      setDraft({ title: '', url: '', description: '', ctaLabel: 'Open', itemType: 'page' });
      await props.reload();
    }
  };

  return (
    <section className="category">
      <div className="category-head">
        <div className="title row">
          <input className="input" style={{ maxWidth: 220, height: 32 }} value={label} onChange={(e) => setLabel(e.target.value)} aria-label="Category label" />
          <input className="input" style={{ flex: 1, minWidth: 200, height: 32 }} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Short description" aria-label="Category description" />
          {dirty && (
            <button className="btn sm primary" onClick={() => props.onSave({ label, description })}>
              Save
            </button>
          )}
        </div>
        <span className={`badge ${c.source === 'manual' ? 'warn' : ''}`}>{c.source}</span>
        <label className="checkbox">
          <input type="checkbox" checked={c.visible} onChange={(e) => props.onSave({ visible: e.target.checked })} /> visible
        </label>
        <button className="btn sm icon" title="Move up" disabled={props.first} onClick={() => props.onMove(-1)}>
          ↑
        </button>
        <button className="btn sm icon" title="Move down" disabled={props.last} onClick={() => props.onMove(1)}>
          ↓
        </button>
        <button className="btn sm" onClick={props.onRescan} disabled={props.rescanning} title="Re-crawl this category's pages and let the AI refresh its cards into a draft">
          {props.rescanning ? 'Re-scanning…' : 'Re-scan category'}
        </button>
        <button className="btn sm danger" onClick={props.onDelete}>
          Delete
        </button>
      </div>
      <div className="items">
        {items.map((it, idx) => (
          <ItemRow key={it.itemId} item={it} first={idx === 0} last={idx === items.length - 1} siblings={items} siteId={props.siteId} reload={props.reload} show={props.show} />
        ))}
        {items.length === 0 && <p className="muted small">No cards. Add one below or re-scan the category.</p>}
        {adding ? (
          <div className="item">
            <span />
            <input className="input" placeholder="Title" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
            <input className="input" placeholder="Description" value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
            <input className="input" placeholder="CTA" value={draft.ctaLabel} onChange={(e) => setDraft({ ...draft, ctaLabel: e.target.value })} />
            <input className="input" placeholder="https://… or tel:…" value={draft.url} onChange={(e) => setDraft({ ...draft, url: e.target.value })} />
            <div className="row">
              <select className="select" style={{ height: 32, width: 110 }} value={draft.itemType} onChange={(e) => setDraft({ ...draft, itemType: e.target.value as Item['itemType'] })}>
                {['page', 'cta', 'lead_form', 'phone', 'email', 'external'].map((t) => (
                  <option key={t}>{t}</option>
                ))}
              </select>
              <button className="btn sm primary" onClick={addItem}>
                Add
              </button>
              <button className="btn sm" onClick={() => setAdding(false)}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div>
            <button className="btn sm" onClick={() => setAdding(true)}>
              + Add card
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

function ItemRow(props: { item: Item; first: boolean; last: boolean; siblings: Item[]; siteId: string; reload: () => Promise<void>; show: (t: string, err?: boolean) => void }) {
  const { item } = props;
  const [v, setV] = useState({ title: item.title, description: item.description, ctaLabel: item.ctaLabel, url: item.url, itemType: item.itemType });
  const dirty = v.title !== item.title || v.description !== item.description || v.ctaLabel !== item.ctaLabel || v.url !== item.url || v.itemType !== item.itemType;
  const save = async (patch: Partial<Item>) => {
    const r = await run(api.updateItem(props.siteId, item.itemId, patch), props.show);
    if (r) await props.reload();
  };
  const move = async (dir: -1 | 1) => {
    const idx = props.siblings.findIndex((x) => x.itemId === item.itemId);
    const other = props.siblings[idx + dir];
    if (!other) return;
    await Promise.all([api.updateItem(props.siteId, item.itemId, { order: other.order }), api.updateItem(props.siteId, other.itemId, { order: item.order })]);
    await props.reload();
  };
  return (
    <div className={`item${item.visible ? '' : ' hidden'}`}>
      <div className="thumb" style={item.thumbnailUrl ? { backgroundImage: `url("${item.thumbnailUrl}")` } : undefined} title={item.thumbnailUrl ? 'Thumbnail from the scan' : 'No thumbnail'} />
      <input className="input" value={v.title} onChange={(e) => setV({ ...v, title: e.target.value })} aria-label="Card title" />
      <input className="input" value={v.description} onChange={(e) => setV({ ...v, description: e.target.value })} placeholder="Description" aria-label="Card description" />
      <input className="input" value={v.ctaLabel} onChange={(e) => setV({ ...v, ctaLabel: e.target.value })} aria-label="Button label" />
      <input className="input mono" value={v.url} onChange={(e) => setV({ ...v, url: e.target.value })} aria-label="URL" />
      <div className="row" style={{ justifyContent: 'flex-end' }}>
        <select className="select" style={{ height: 32, width: 104 }} value={v.itemType} onChange={(e) => setV({ ...v, itemType: e.target.value as Item['itemType'] })} aria-label="Type">
          {['page', 'cta', 'lead_form', 'phone', 'email', 'external'].map((t) => (
            <option key={t}>{t}</option>
          ))}
        </select>
        {dirty ? (
          <button className="btn sm primary" onClick={() => save(v)}>
            Save
          </button>
        ) : (
          <span className={`badge ${item.source === 'manual' ? 'warn' : ''}`} title={item.confidence !== undefined ? `confidence ${(item.confidence * 100).toFixed(0)}%` : undefined}>
            {item.source}
          </span>
        )}
        <label className="checkbox" title="Visible in the widget">
          <input type="checkbox" checked={item.visible} onChange={(e) => save({ visible: e.target.checked })} />
        </label>
        <button className="btn sm icon" disabled={props.first} onClick={() => move(-1)} title="Move up">
          ↑
        </button>
        <button className="btn sm icon" disabled={props.last} onClick={() => move(1)} title="Move down">
          ↓
        </button>
        <button
          className="btn sm icon danger"
          title="Delete card"
          onClick={async () => {
            if (!confirm(`Delete "${item.title}"?`)) return;
            const r = await run(api.deleteItem(props.siteId, item.itemId), props.show, 'Card deleted');
            if (r !== null) await props.reload();
          }}
        >
          ×
        </button>
      </div>
    </div>
  );
}
